import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { IResponseDto } from '@models/exceptions/response-dto';
import { IPage } from '@models/pagination/page';
import { PageRequest } from '@models/pagination/page-request';
import { IAddressDto } from '@models/portal/users/dtos/address-dto';
import { IAttachmentDto } from '@models/portal/users/dtos/attachment-dto';
import { IContactDto } from '@models/portal/users/dtos/contact-dto';
import { IDocumentDto } from '@models/portal/users/dtos/document-dto';
import { IUserDto } from '@models/portal/users/dtos/user-dto';
import { IUserRelationshipDto } from '@models/portal/users/dtos/user-relationship-dto';
import { IUserResponsibleDto } from '@models/portal/users/dtos/user-responsible-dto';
import { IUserFilterDto } from '@models/portal/users/filters/user-filter-dto';
import { AddressForm } from '@models/portal/users/forms/address-form';
import { AttachmentForm } from '@models/portal/users/forms/attachment-form';
import { BaseAggregateForm } from '@models/portal/users/forms/base-aggregate-form';
import { ContactForm } from '@models/portal/users/forms/contact-form';
import { DocumentForm } from '@models/portal/users/forms/document-form';
import { UserForm } from '@models/portal/users/forms/user-form';
import { UserRelationshipForm } from '@models/portal/users/forms/user-relationship-form';
import { UserFilter } from '@models/registration/users/users-filter';
import { extractResponse } from '@shared/rxjs/custom-rxjs-operators';
import { Observable } from 'rxjs';
import { toParams, url } from 'src/app/core/utilities/utility-functions';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  #baseUrl = environment.apiUrl.concat('/v1/usuarios')

  #http = inject(HttpClient);

  manageRegisterUserReference$(form: UserRelationshipForm): Observable<IUserRelationshipDto> {
    return this.#http.post<IResponseDto<IUserRelationshipDto>>(url(this.#baseUrl, 'completos'), form).pipe(extractResponse());
  }

  manageUpdateUserReference$(form: UserRelationshipForm, usuNrId: number): Observable<IUserRelationshipDto> {
    return this.#http.put<IResponseDto<IUserRelationshipDto>>(url(this.#baseUrl, 'completos', usuNrId), form).pipe(extractResponse());
  }

  fetchCompleteUserReference$(usuNrId: number): Observable<IUserRelationshipDto> {
    return this.#http.get<IResponseDto<IUserRelationshipDto>>(url(this.#baseUrl, 'completos', usuNrId)).pipe(extractResponse());
  }

  fetchUsers$(filter: UserFilter, pageRequest: PageRequest): Observable<IPage<IUserDto>> {
    return this.#http.get<IResponseDto<IPage<IUserDto>>>(this.#baseUrl, { params: toParams(filter, pageRequest) }).pipe(extractResponse());
  }

  fetchCompleteUser$(usuNrId: number): Observable<IUserDto> {
    return this.#http.get<IResponseDto<IUserDto>>(url(this.#baseUrl, usuNrId)).pipe(extractResponse());
  }

  registerUser$(form: UserForm): Observable<IUserDto> {
    return this.#http.post<IResponseDto<IUserDto>>(this.#baseUrl, form).pipe(extractResponse());
  }

  updateUser$(form: UserForm): Observable<IUserDto> {
    return this.#http.put<IResponseDto<IUserDto>>(this.#baseUrl, form).pipe(extractResponse());
  }

  deleteUser$(form: UserForm): Observable<void> {
    return this.#http.delete<IResponseDto<void>>(this.#baseUrl, { body: form }).pipe(extractResponse());
  }

  fetchUserResponsible$(usuNrId: number): Observable<IPage<IUserResponsibleDto>> {
    return this.#http.get<IResponseDto<IPage<IUserResponsibleDto>>>(url(this.#baseUrl, usuNrId, 'responsaveis')).pipe(extractResponse());
  }

  fetchUserAddress$(usuNrId: number): Observable<IAddressDto[]> {
    return this.#http.get<IResponseDto<IAddressDto[]>>(url(this.#baseUrl, usuNrId, 'logradouros')).pipe(extractResponse());
  }

  registerUserAddress$(form: BaseAggregateForm<AddressForm>): Observable<IAddressDto> {
    return this.#http.post<IResponseDto<IAddressDto>>(url(this.#baseUrl, 'logradouros'), form).pipe(extractResponse());
  }

  updateUserAddress$(form: BaseAggregateForm<AddressForm>): Observable<IAddressDto> {
    return this.#http.put<IResponseDto<IAddressDto>>(url(this.#baseUrl, 'logradouros'), form).pipe(extractResponse());
  }

  deleteUserAddress$(form: BaseAggregateForm<AddressForm>): Observable<void> {
    return this.#http.delete<IResponseDto<void>>(url(this.#baseUrl, 'logradouros'), { body: form }).pipe(extractResponse());
  }

  fetchUserContacts$(usuNrId: number): Observable<IContactDto[]> {
    return this.#http.get<IResponseDto<IContactDto[]>>(url(this.#baseUrl, usuNrId, 'contatos')).pipe(extractResponse());
  }

  manageUserContacts$(form: BaseAggregateForm<ContactForm>): Observable<IContactDto[]> {
    return this.#http.post<IResponseDto<IContactDto[]>>(url(this.#baseUrl, 'contatos'), form).pipe(extractResponse());
  }

  fetchUserDocuments$(usuNrId: number): Observable<IDocumentDto[]> {
    return this.#http.get<IResponseDto<IDocumentDto[]>>(url(this.#baseUrl, usuNrId, 'documentos')).pipe(extractResponse());
  }

  manageUserDocuments$(form: BaseAggregateForm<DocumentForm>): Observable<IDocumentDto[]> {
    return this.#http.post<IResponseDto<IDocumentDto[]>>(url(this.#baseUrl, 'documentos'), form).pipe(extractResponse());
  }

  fetchUserAttachments$(usuNrId: number): Observable<IAttachmentDto[]> {
    return this.#http.get<IResponseDto<IAttachmentDto[]>>(url(this.#baseUrl, usuNrId, 'anexos')).pipe(extractResponse());
  }

  manageUserAttachments$(form: BaseAggregateForm<AttachmentForm>): Observable<void> {
    return this.#http.post<IResponseDto<void>>(url(this.#baseUrl, 'anexos'), form).pipe(extractResponse());
  }

  fetchResponsibles$(filters: IUserFilterDto, pageRequest: PageRequest): Observable<IPage<IUserDto>> {
    return this.#http.get<IResponseDto<IPage<IUserDto>>>(url(this.#baseUrl, 'simples'), { params: toParams(filters, pageRequest) }).pipe(extractResponse());
  }

  generateNewPasswordForUser(usuNrId: number): Observable<any> {
    return this.#http.patch<any>(`${this.#baseUrl}/gerar-nova-senha/${usuNrId}`, {});
  }
}

